import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AreaChart, BarList, Title } from '@tremor/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { KpiCard } from '@/components/kpi-card';
import { DateRangePicker } from '@/components/date-range-picker';
import { Combobox } from '@/components/combobox';
import { SearchBox } from '@/components/search-box';
import { useSalesFilters } from '@/lib/sales-filters';
import { getSummary, getSalesMasters, type TopAxis, type MatchMode } from '@/lib/sales-api';
import { fmtINR, fmtNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/sales/dashboard')({ component: Page });

function Page() {
  const { from, to, store, setFilters } = useSalesFilters();
  const [topAxis, setTopAxis] = React.useState<TopAxis>('item');
  // Breakdown filters — these narrow the "Top by revenue" card only; the KPI
  // row stays per-bill. Until now they were local state wired to nothing.
  const [itemFilter, setItemFilter] = React.useState<string | null>(null);
  const [brandFilter, setBrandFilter] = React.useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = React.useState<string | null>(null);
  const [styleFilter, setStyleFilter] = React.useState<string | null>(null);
  const [q, setQ] = React.useState<string | null>(null);
  const [match, setMatch] = React.useState<MatchMode>('contains');
  const hasTopFilters = Boolean(itemFilter || brandFilter || categoryFilter || styleFilter || q);

  const mastersQ = useQuery({
    queryKey: ['sales:masters'],
    queryFn: getSalesMasters,
    staleTime: 5 * 60_000,
  });

  const summaryQ = useQuery({
    queryKey: ['sales:summary', from, to, store, itemFilter, brandFilter, categoryFilter, styleFilter, q, match],
    queryFn: () => getSummary({
      from, to, store,
      item: itemFilter, brand: brandFilter, category: categoryFilter,
      style: styleFilter, q, match,
    }),
    staleTime: 30_000,
  });

  const drillSearch = { from, to, ...(store ? { store } : {}) };

  return (
    <div className="space-y-6">
      {/* Filters row - all aligned together */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-4">
          <div>
            <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Date Range
            </label>
            <DateRangePicker from={from} to={to} onChange={(r) => setFilters({ from: r.from, to: r.to })} />
          </div>
          <Combobox
            label="Item"
            value={itemFilter ?? ''}
            options={mastersQ.data?.item ?? []}
            onChange={(v) => setItemFilter(v || null)}
            placeholder="All items"
            className="min-w-[160px]"
          />
          <Combobox
            label="Brand"
            value={brandFilter ?? ''}
            options={mastersQ.data?.brand ?? []}
            onChange={(v) => setBrandFilter(v || null)}
            placeholder="All brands"
            className="min-w-[160px]"
          />
          <Combobox
            label="Article"
            value={styleFilter ?? ''}
            options={mastersQ.data?.style ?? []}
            onChange={(v) => setStyleFilter(v || null)}
            placeholder="All articles"
            className="min-w-[160px]"
          />
          <Combobox
            label="Category"
            value={categoryFilter ?? ''}
            options={mastersQ.data?.category ?? []}
            onChange={(v) => setCategoryFilter(v || null)}
            placeholder="All categories"
            className="min-w-[180px]"
          />
          <SearchBox
            q={q}
            onQChange={(v) => setQ(v ?? null)}
            match={match}
            onMatchChange={setMatch}
            placeholder="Item, brand, article, group, sub-group…"
          />
          {hasTopFilters && (
            <button
              type="button"
              onClick={() => {
                setItemFilter(null); setBrandFilter(null); setCategoryFilter(null);
                setStyleFilter(null); setQ(null);
              }}
              className="h-10 rounded-md px-3 text-xs font-medium text-muted-foreground hover:text-ink"
            >
              Clear filters
            </button>
          )}
        </CardContent>
      </Card>

      {summaryQ.isLoading && <p>Loading…</p>}
      {summaryQ.error && <p className="text-sm text-destructive">{(summaryQ.error as Error).message}</p>}

      {summaryQ.data && (
        <>
          {/* KPI cards - 4 cards only (removed Stores) */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KpiCard label="Revenue" value={fmtINR(summaryQ.data.kpis.revenue)} to="/sales/bills" search={drillSearch} />
            <KpiCard label="Orders" value={fmtNumber(summaryQ.data.kpis.orders)} to="/sales/bills" search={drillSearch} />
            <KpiCard label="AOV" value={fmtINR(summaryQ.data.kpis.aov)} hint="Average bill value" to="/sales/bills" search={drillSearch} />
            <KpiCard label="Returns" value={fmtNumber(summaryQ.data.kpis.returnsCount)} hint={fmtINR(summaryQ.data.kpis.returnsAmount)} to="/sales/returns" search={drillSearch} />
          </div>

          <Card>
            <CardHeader><CardTitle>Daily revenue</CardTitle></CardHeader>
            <CardContent>
              <AreaChart
                className="h-72"
                data={summaryQ.data.daily.map((p) => ({ date: p.date, Revenue: p.revenue, Orders: p.orders }))}
                index="date"
                categories={['Revenue']}
                valueFormatter={(n: number) => fmtINR(n)}
                showLegend={false}
                yAxisWidth={64}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
              <CardTitle>
                Top by revenue
                {hasTopFilters && (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">· filtered</span>
                )}
              </CardTitle>
              <div
                role="radiogroup"
                aria-label="Group top revenue by"
                className="flex w-full items-center rounded-full border bg-background p-0.5 sm:w-auto"
              >
                {(['item', 'brand', 'group', 'category', 'subgroup'] as const).map((a) => {
                  const selected = topAxis === a;
                  return (
                    <button
                      key={a}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setTopAxis(a)}
                      className={cn(
                        'flex-1 rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors sm:flex-initial',
                        selected ? 'bg-ink text-white shadow-sm' : 'text-muted-foreground hover:text-ink',
                      )}
                    >
                      {a === 'subgroup' ? 'Sub-group' : a}
                    </button>
                  );
                })}
              </div>
            </CardHeader>
            <CardContent>
              <Title className="mb-2 text-sm font-normal text-muted-foreground">
                Qty in legend; revenue on bar
                {hasTopFilters && ' · filters apply to this breakdown, not the KPI row above'}
              </Title>
              {(() => {
                // Prefer the new topBy.* arrays; gracefully fall back to
                // topItems when the API response predates the multi-axis
                // change (mostly to keep cached-response renders smooth).
                const rows = summaryQ.data.topBy?.[topAxis] ?? (topAxis === 'item' ? summaryQ.data.topItems : []);
                if (rows.length === 0) {
                  return (
                    <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                      No {topAxis === 'subgroup' ? 'sub-group' : topAxis} data for this range.
                    </p>
                  );
                }
                return (
                  <BarList
                    data={rows.map((t) => ({ name: `${t.name} · ${fmtNumber(t.qty)} pcs`, value: t.revenue }))}
                    valueFormatter={(n: number) => fmtINR(n)}
                  />
                );
              })()}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
